import { isNumber, get, isString, isEmpty, isArray, groupBy, sumBy, isBoolean, isNull, isUndefined } from "lodash";

import { ITableData, ITableColumn, ColumnType } from "../types/table-type";
import { ChartType, IBasicData } from "../types/global-type";
import { IChartFormValue, StatisticsType, TimeCycleType } from "../types/form-type";

import { GROUP_KEY_SPLICING, GROUP_EMPTY_KEY } from "../constants/app";
import { IS_PIE_CHART } from "../constants/chart";

import FormatByIndex from "./FormatByIndex";

import { getTimeCycleStr, convertTimeCycle } from "../lib/day";
import { formatMessage } from "../lib/intl";
import { checkValueInArr } from "../utils/form_secondary";
import { getFormatValueFunc, getFormatDimensionFunc, getRealColumn, checkIsDateCycleGroup } from "../utils/option_main";
import { arrayToObject, isDateType } from "../utils/tableData_secondary";
import {
  getValueByCalculationType,
  formatDimensionToString,
  formatDimensionToStringKey,
  checkGroupId,
} from "../utils/option_secondary";

interface IProps {
  tableData: ITableData;
  formValue: IChartFormValue;
}

class CalculationDataByChart {
  private tableData: ITableData;
  private formValue: IChartFormValue;
  public formatValue: (value: number) => string;
  public formatDimension: (value: any) => string;
  public formatGroup: (value: any) => string;
  public formatByIndex = new FormatByIndex();
  public isGroup: boolean;
  public isDateCycleGroup: boolean;

  constructor(props: IProps) {
    const { tableData, formValue } = props;
    this.tableData = tableData;
    this.formValue = formValue;
    this.formatValue = getFormatValueFunc(tableData, formValue.statisticsType, formValue.calculationTargetId);
    this.formatDimension = getFormatDimensionFunc(tableData, formValue.dimensionId);
    this.formatGroup = getFormatDimensionFunc(tableData, formValue.stackByColumnId || "");
    this.isGroup = !!checkGroupId(formValue, tableData, formValue.chartType);

    const { dimensionId } = this.formValue;
    const dimensionColumn = getRealColumn(dimensionId, this.tableData);
    // 日期列开启按时间粒度分组
    if (
      !!dimensionColumn &&
      isDateType(dimensionColumn.type) &&
      dimensionId === this.formValue.dimensionId &&
      this.formValue.isGroupAtCycle
    ) {
      // 整合到 formatByIndex.set 那里
      this.formatDimension = (value: any) => getTimeCycleStr(value, this.formValue.groupAtCycle);
    }
    this.isDateCycleGroup = checkIsDateCycleGroup(tableData, formValue);
  }

  // 获取用于 group 的 key 需要指定 columnId 如果有多个 key 则调用多次本函数 再进行拼接
  getValueStr(data: IBasicData, columnId?: string): string | number | undefined {
    if (!columnId) return data.cellValue;
    const column = getRealColumn(columnId, this.tableData);
    if (!column) return data.cellValue;
    if (isDateType(column.type) && columnId === this.formValue.dimensionId && this.formValue.isGroupAtCycle) {
      return getTimeCycleStr(data[columnId], this.formValue.groupAtCycle);
    }
    return formatDimensionToStringKey(data[columnId], column);
  }

  getValueStrByIds(data: IBasicData, ids?: string[]): string | number | undefined {
    if (!isArray(ids) || ids.length === 0) return undefined;
    const keys = ids.map((item) => this.getValueStr(data, item)).join(GROUP_KEY_SPLICING);
    return keys;
  }

  // 过滤空值 根据指定的字段
  filterEmpty(dataList: IBasicData[], key: string): IBasicData[] {
    const result = dataList.filter((item) => {
      const value = get(item, key);
      if (isNumber(value) && !isNaN(value)) return true;
      if (isString(value) && value.length > 0) return true;
      if (isBoolean(value)) return true;
      if (!isEmpty(value)) return true;
      return false;
    });
    return result;
  }

  // 强制过滤 checkbox 的非法值
  filterCheckboxEmpty(dataList: IBasicData[], key: string): IBasicData[] {
    const result = dataList.filter((item) => {
      const checkboxData = get(item, key);
      return isArray(checkboxData) && checkboxData.length === 0 ? false : true;
    });
    return result;
  }

  // 分组并计算
  groupCalc(dataList: IBasicData[], keyList: string[]) {
    const { statisticsType, calculationType } = this.formValue;
    const temp = groupBy(dataList, (data: IBasicData) => this.getValueStrByIds(data, keyList));
    const result = Object.values(temp).map((item) => {
      const obj = {
        ...item[0],
        cellValue:
          statisticsType === StatisticsType.COUNT
            ? sumBy(item, "cellValue")
            : getValueByCalculationType(
                item.map((item) => item.cellValue),
                calculationType
              ),
      };
      return obj;
    });
    return result;
  }

  /**
   * 转换为旧结构 对于数组 直接拼接
   * @param dataList
   * @param keyList
   * @param isGlobalFormat 是否开启 format
   * @returns
   */
  convertOldStructure(
    dataList: IBasicData[],
    keyList: string[],
    isGlobalFormat?: boolean
  ): { [key in string]: number | string }[] {
    const emptyText = formatMessage({ id: "other.dimension.name.default" });
    const result = dataList.map((item) => {
      const obj = keyList.reduce<IBasicData>(
        (acc, currValue, index) => {
          const column = getRealColumn(currValue, this.tableData);
          // 为了避免重名 这里应该根据 id 而非 name 计算 key
          let currValueToString: string;
          if (index === 0 && this.isDateCycleGroup) {
            currValueToString = getTimeCycleStr(item[currValue], this.formValue.groupAtCycle);
          } else if (index === 1 && item[currValue] === GROUP_EMPTY_KEY) {
            currValueToString = emptyText;
          } else {
            currValueToString = formatDimensionToString(item[currValue], column as ITableColumn);
          }
          const currValueKey = formatDimensionToStringKey(item[currValue], column as ITableColumn);
          this.formatByIndex.set(currValueKey, currValueToString);
          const isFormat = isGlobalFormat || index > 0;
          return {
            ...acc,
            [currValue]: isFormat ? currValueKey : item[currValue],
          };
        },
        {
          ...item,
          value: item.cellValue,
        }
      );
      if (item["order"]) {
        obj["order"] = item["order"];
      }
      return obj;
    });
    return result;
  }

  convertOldDataToSource(list: { [key in string]: number | string }[]): { [key in string]: number | string }[] {
    const result = list.map((item: { [key in string]: number | string }) => {
      return {
        ...item,
      };
    });
    return result;
  }

  // 折线图 的x轴为非类目轴时 接受的x轴比如是有意义的 number dateString 此时不能使用 format
  checkGlobalFormat() {
    const { dimensionId, chartType } = this.formValue;
    // 坑啊 饼图绘制的时候 第一个维度列 如果是 number 会用于计算 扇形角度 所以饼图的 dimensionValue 必须 format
    if (IS_PIE_CHART[chartType]) return true;
    const dimensionColumn = getRealColumn(dimensionId, this.tableData);

    if (!dimensionColumn) return false;

    if (
      checkValueInArr(chartType, [ChartType.BASIC_LINE]) &&
      checkValueInArr(dimensionColumn.type, [
        ColumnType.DATETIME,
        ColumnType.NUMBER,
        ColumnType.RATING,
        ColumnType.CURRENCY,
        ColumnType.CREATED_AT,
      ])
    )
      return false;

    return true;
  }

  // 当 第一个维度 是 单选 or 开启拆分的多选 加入 对应的order
  setOrderBySelect(dataList: IBasicData[], dimensionKey: string): IBasicData[] {
    const dimensionColumn = getRealColumn(dimensionKey, this.tableData);
    if (!dimensionColumn) return dataList;
    const {
      typeOptions: { options },
    } = dimensionColumn as ITableColumn<ColumnType.SELECT>;
    if (!isArray(options)) return dataList;
    const orderByOptionId = arrayToObject(options, "optionId");
    const newDataList = dataList.map((item) => {
      const newData = {
        ...item,
        order: orderByOptionId[item[dimensionKey]]?.order,
      };
      return newData;
    });
    return newDataList;
  }

  setOrderByStatus(dataList: IBasicData[], dimensionKey: string): IBasicData[] {
    const dimensionColumn = getRealColumn(dimensionKey, this.tableData);
    if (!dimensionColumn) return dataList;
    const {
      typeOptions: { statuses },
    } = dimensionColumn as ITableColumn<ColumnType.STATUS>;
    if (!isArray(statuses)) return dataList;
    const orderByOptionId = arrayToObject(statuses, "statusId");
    const newDataList = dataList.map((item) => {
      const newData = {
        ...item,
        order: orderByOptionId[item[dimensionKey]]?.order,
      };
      return newData;
    });
    return newDataList;
  }

  convertValueEmpty(source: (string | number)[][], valueIndex: number) {
    return source.map((item) => {
      const newArr = [...item];
      if (newArr[valueIndex] === 0) {
        // @ts-ignore
        newArr[valueIndex] = undefined;
      }
      return newArr;
    });
  }

  // 当开启按时间粒度分组时 转化 dimension 的 value
  convertDimensionByDateCycle(list: IBasicData[], key: string, groupAtCycle: TimeCycleType): IBasicData[] {
    const result = list.map((item) => ({
      ...item,
      [key]: convertTimeCycle(item[key], groupAtCycle),
    }));
    return result;
  }

  // 饼图需要过滤 value 因为 负数 用于扇形角度计算 会带来问题 0会渲染出没有指向扇形的 labelLine
  filterValuePositiveNumber(list: IBasicData[], valueKey: string): IBasicData[] {
    return list.filter((item) => {
      const value = item[valueKey];
      if (isNumber(value) && value <= 0) {
        return false;
      }
      return true;
    });
  }

  convertGroupEmpty(list: IBasicData[], gropuId: string): IBasicData[] {
    const result = list.map((item) => ({
      ...item,
      [gropuId]:
        item[gropuId] === "" || isNull(item[gropuId]) || isUndefined(item[gropuId]) ? GROUP_EMPTY_KEY : item[gropuId],
    }));
    return result;
  }
}

export default CalculationDataByChart;
